import { Link, useParams, useSearchParams } from "react-router-dom";
import { useEffect, useState, type ReactNode } from "react";
import type { LucideIcon } from "lucide-react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import {
  ArrowDownRight,
  ArrowUpRight,
  CalendarClock,
  CircleCheck,
  Handshake,
  Plus,
  ReceiptText,
  Settings,
  UserPlus,
  Users,
} from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Skeleton } from "../components/ui/skeleton";
import { Button } from "../components/ui/button";
import { SectionNav, type SectionNavItem } from "../components/ui/section-nav";
import { MoneyAmount } from "../components/MoneyAmount";
import { GroupSettingsPanel } from "../components/GroupSettingsPanel";
import { ParticipantsManager } from "../components/ParticipantsManager";
import { InviteLinkCard } from "../components/InviteLinkCard";
import { JoinRequestsPanel } from "../components/JoinRequestsPanel";
import { RecurringExpensesPanel } from "../components/RecurringExpensesPanel";
import { GroupActivityTimeline } from "../components/GroupActivityTimeline";
import {
  useExpenses,
  useGroup,
  useGroupBalances,
  useInfiniteExpenses,
  useInfiniteSettlements,
  useJoinRequests,
  useMe,
  useMyGroups,
  usePublicGroupInvite,
  useRecurringExpenses,
} from "../api/hooks";
import { canManageGroup, getMyGroupRole } from "../lib/groupRole";
import type { Group } from "../api/types";

type GroupSection = "activity" | "recurring" | "people" | "settings";

const GROUP_SECTIONS: GroupSection[] = [
  "activity",
  "recurring",
  "people",
  "settings",
];

function isGroupSection(value: string | null): value is GroupSection {
  return value !== null && (GROUP_SECTIONS as string[]).includes(value);
}

/**
 * Resolves the `?section=` deep link. `expenses` and `settle` were the two tabs
 * that became one activity timeline (issue #453); links to either — including
 * ones already in the wild, like the post-settlement redirect — land on it
 * rather than falling over.
 */
function resolveSection(value: string | null): GroupSection {
  if (isGroupSection(value)) return value;
  return "activity";
}

export function GroupDetailPage(): ReactNode {
  const { groupId } = useParams<{ groupId: string }>();
  const [searchParams] = useSearchParams();
  const groupQ = useGroup(groupId);
  const expensesQ = useExpenses(groupId);
  const meQ = useMe();
  const myGroupsQ = useMyGroups();
  const reduceMotion = useReducedMotion();
  const isDesktop = useDesktopLayout();
  // Deep-link support: `?section=settle` (e.g. the redirect after recording a
  // payment) opens that tab; otherwise default to Expenses.
  const initialSectionParam = searchParams.get("section");
  const [section, setSection] = useState<GroupSection>(
    resolveSection(initialSectionParam),
  );
  // Direction the section panel slides in from (+1 forward, -1 back). Computed
  // when the section changes — an event, where we know both the old and new
  // section — rather than by tracking the previous section via a ref during
  // render (which the new section animates the wrong way on an unrelated
  // re-render, and which `react-hooks/refs` rightly flags).
  const [direction, setDirection] = useState(1);
  const [addingParticipant, setAddingParticipant] = useState(false);

  function changeSection(next: GroupSection): void {
    setDirection(
      GROUP_SECTIONS.indexOf(next) >= GROUP_SECTIONS.indexOf(section) ? 1 : -1,
    );
    setSection(next);
  }

  const myRole = groupQ.data ? getMyGroupRole(groupQ.data, meQ.data) : null;
  const isOwner = canManageGroup(myRole);
  // Pending join requests drive the People nav badge; only owners can see it.
  const joinRequestsQ = useJoinRequests(groupId, { enabled: isOwner });
  // Warm every tab's query cache during the initial group load so switching
  // sections never has to wait for its first request.
  useInfiniteExpenses(groupId);
  useRecurringExpenses(groupId);
  useGroupBalances(groupId);
  useInfiniteSettlements(groupId);
  usePublicGroupInvite(isOwner ? groupId : undefined);

  if (groupQ.isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-32" />
      </div>
    );
  }
  if (!groupQ.data) {
    return (
      <Card tone="ghost">
        <CardContent className="py-12 text-center text-on-surface-variant">
          Group not found.
        </CardContent>
      </Card>
    );
  }

  const group = groupQ.data;
  const expenseCount = expensesQ.data?.items.length ?? 0;
  const balance = myGroupsQ.data?.items.find(
    (item) => item.id === group.id,
  )?.balance;
  const canCreateExpense = myRole === "OWNER" || myRole === "MEMBER";
  const pendingRequests = isOwner ? (joinRequestsQ.data?.items.length ?? 0) : 0;
  const enterOffset = isDesktop
    ? { opacity: 0, y: direction * 12 }
    : { opacity: 0, x: direction * 12 };
  const primaryAction =
    canCreateExpense && section === "activity"
      ? {
          label: "New expense",
          icon: Plus,
          to: `/groups/${group.id}/expenses/new`,
        }
      : canCreateExpense && section === "recurring"
        ? {
            label: "Add recurring",
            icon: Plus,
            to: `/groups/${group.id}/recurring-expenses/new`,
          }
        : isOwner && section === "people"
          ? {
              label: "Add participant",
              icon: UserPlus,
              onClick: () => setAddingParticipant(true),
            }
          : null;

  const navItems: SectionNavItem<GroupSection>[] = [
    { key: "activity", label: "Activity", icon: ReceiptText },
    { key: "recurring", label: "Recurring", icon: CalendarClock },
    { key: "people", label: "People", icon: Users, badge: pendingRequests },
    { key: "settings", label: "Settings", icon: Settings },
  ];

  const summaryCards = (
    <Card className="overflow-hidden bg-primary text-on-primary">
      <CardContent className="p-0">
        <div className="flex flex-wrap items-center gap-2 px-6 pb-4 pt-6 sm:px-8">
          <span className="rounded-full bg-on-primary/15 px-3 py-1.5 text-label-md">
            {group.currency}
          </span>
          <span className="rounded-full bg-on-primary/10 px-3 py-1.5 text-label-md text-on-primary/85">
            {group.participants.length}{" "}
            {group.participants.length === 1 ? "person" : "people"}
          </span>
          {expensesQ.isLoading ? (
            <Skeleton
              aria-label="Loading expenses"
              className="h-8 w-28 rounded-full bg-on-primary/15"
            />
          ) : (
            <span className="rounded-full bg-on-primary/10 px-3 py-1.5 text-label-md text-on-primary/85">
              {expenseCount} {expenseCount === 1 ? "expense" : "expenses"}
            </span>
          )}
        </div>
        <GroupBalance
          balance={balance}
          currency={group.currency}
          settleUpTo={`/groups/${group.id}/settle`}
        />
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-8 pb-[calc(7rem+env(safe-area-inset-bottom))] scroll-pb-[calc(7rem+env(safe-area-inset-bottom))] md:pb-0 md:scroll-pb-0">
      <header className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <p className="text-label-sm text-on-surface-variant">Group</p>
          <h1 className="text-headline-lg">{group.label}</h1>
        </div>
        <AnimatePresence mode="wait">
          {primaryAction ? (
            <motion.div
              key={section}
              initial={reduceMotion ? false : { opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={reduceMotion ? undefined : { opacity: 0, y: -2 }}
              transition={{ duration: 0.14, ease: "easeOut" }}
            >
              <GroupPrimaryAction {...primaryAction} />
            </motion.div>
          ) : null}
        </AnimatePresence>
      </header>

      {/*
        Sidebar section nav on desktop, floating bottom bar on mobile. Owners
        get all four sections; non-owners can view People but can't manage the
        group, so they don't get Settings.
      */}
      <div className="flex flex-col gap-6 md:flex-row md:gap-8">
        <div className="md:w-48 md:shrink-0">
          <SectionNav
            items={isOwner ? navItems : navItems.slice(0, 3)}
            value={section}
            onChange={changeSection}
          />
        </div>

        {/*
          Enter-only, keyed on the active section. Like the top-level route
          transition, we avoid `mode="wait"` so the incoming tab paints over
          the outgoing one instead of leaving an empty content area (the
          "flash") while the exit animation runs.
        */}
        <motion.div
          key={section}
          initial={reduceMotion ? false : enterOffset}
          animate={{ opacity: 1, x: 0, y: 0 }}
          transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
          className="min-w-0 flex-1 space-y-8"
        >
          {section === "activity" ? (
            <>
              {summaryCards}
              {/* Expenses and payments in one list (issue #453); "who owes
                who" moved to its own Settle up screen. */}
              <GroupActivityTimeline group={group} />
            </>
          ) : null}

          {section === "recurring" ? (
            <RecurringExpensesPanel
              group={group}
              canManage={canCreateExpense}
            />
          ) : null}

          {section === "people" ? (
            <>
              <ParticipantsManager
                group={group}
                canManage={isOwner}
                adding={addingParticipant}
                onAddingChange={setAddingParticipant}
              />
              {isOwner ? (
                <>
                  <InviteLinkCard group={group} />
                  <JoinRequestsPanel group={group} />
                </>
              ) : null}
            </>
          ) : null}

          {isOwner && section === "settings" ? (
            <GroupSettingsPanel group={group} />
          ) : null}
        </motion.div>
      </div>
    </div>
  );
}

function GroupBalance({
  balance,
  currency,
  settleUpTo,
}: {
  balance: number | undefined;
  currency: Group["currency"];
  /** Route to the Settle up screen — the group's "what's owed" view. */
  settleUpTo: string;
}): ReactNode {
  if (balance === undefined) {
    return (
      <div className="border-t border-on-primary/15 bg-on-primary/8 px-6 py-6 sm:px-8">
        <p className="text-label-sm text-on-primary/70">Your balance</p>
        <Skeleton className="mt-4 h-12 w-44 bg-on-primary/15" />
        <p className="mt-3 text-label-md text-on-primary/70">
          Loading your balance
        </p>
      </div>
    );
  }

  const isOwed = balance > 0;
  const isOwing = balance < 0;
  const Icon = isOwed ? ArrowUpRight : isOwing ? ArrowDownRight : CircleCheck;
  const label = isOwed ? "You are owed" : isOwing ? "You owe" : "Your balance";
  const detail = isOwed
    ? "This group owes you"
    : isOwing
      ? "Your share still to pay"
      : "Everyone is settled up";

  return (
    <div className="border-t border-on-primary/15 bg-on-primary/8 px-6 py-6 sm:px-8">
      <div className="flex items-center gap-2 text-on-primary/70">
        <span className="grid size-7 place-items-center rounded-full bg-on-primary/12">
          <Icon className="size-4" />
        </span>
        <p className="text-label-sm">{label}</p>
      </div>
      <MoneyAmount
        minor={Math.abs(balance)}
        currency={currency}
        className="mt-4 block text-[clamp(2.25rem,10vw,3.5rem)] font-extrabold leading-none tracking-[-0.035em]"
      />
      <p className="mt-3 text-label-md text-on-primary/70">{detail}</p>
      {/* The settle-up entry point: a preview of what's owed, not the history
          (issue #453). Shown even when settled so the plan is always one tap
          away. */}
      <Link to={settleUpTo} className="mt-5 inline-block">
        <Button
          variant="secondary"
          className="gap-2 bg-on-primary/15 text-on-primary hover:bg-on-primary/25"
        >
          <Handshake className="size-4" />
          Settle up
        </Button>
      </Link>
    </div>
  );
}

function useDesktopLayout(): boolean {
  const [isDesktop, setIsDesktop] = useState(false);

  useEffect(() => {
    if (!window.matchMedia) return;

    const mediaQuery = window.matchMedia("(min-width: 768px)");
    const update = () => setIsDesktop(mediaQuery.matches);
    update();
    mediaQuery.addEventListener("change", update);
    return () => mediaQuery.removeEventListener("change", update);
  }, []);

  return isDesktop;
}

function GroupPrimaryAction({
  icon: Icon,
  label,
  to,
  onClick,
}: {
  icon: LucideIcon;
  label: string;
  to?: string;
  onClick?: () => void;
}): ReactNode {
  const content = (
    <>
      <Icon className="size-4 shrink-0" />
      <span className="text-label-md">{label}</span>
    </>
  );
  const buttonClassName = "h-11 w-44 gap-2";

  return to ? (
    <Link to={to}>
      <Button className={buttonClassName}>{content}</Button>
    </Link>
  ) : (
    <Button className={buttonClassName} onClick={onClick}>
      {content}
    </Button>
  );
}
